import { useCallback, useEffect, useState } from 'react'
import logoBlue from '../../assets/image/logo/logo.svg'
import useTranslation from '../hooks/useTranslation'
import ContactButton from './ui/ContactButton'
import LargeContainer from '../UI/Container/LargeContainer'

type LandingHeaderProps = {
  resetActiveId: () => void
}

const HEADER_MAIN_HEIGHT = 50
const HEADER_TOP_BAR_HEIGHT = 22

const LandingHeaderNew = ({ resetActiveId }: LandingHeaderProps) => {
  const t = useTranslation()
  const f = t.landing.footer
  const [mobileOpen, setMobileOpen] = useState(false)

  const headerOffset = HEADER_MAIN_HEIGHT + HEADER_TOP_BAR_HEIGHT
  const closeMobile = useCallback(() => setMobileOpen(false), [])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [mobileOpen, closeMobile])

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-blue text-white">
        <LargeContainer>
          <div className="flex items-center justify-end gap-4 py-[1px] text-sm sm:gap-6 sm:text-base">
            <a href={f.phoneHref} className="text-white no-underline hover:underline !text-sm font-[400]">
              {f.phone}
            </a>
            <a href={f.emailHref} className="text-white no-underline hover:underline !text-sm font-[400]">
              {f.email}
            </a>
          </div>
        </LargeContainer>
      </div>

      <div className="bg-white shadow-lg">
        <LargeContainer>
          <div className="flex min-h-[50px] flex-wrap items-center justify-between gap-5 -ml-3">
            <a onClick={resetActiveId} className="flex shrink-0 items-center" href="#intro" aria-label="MPS ARCHONIC – Design and Build">
              <img src={logoBlue} alt="Design and Build" className="h-9 w-auto xl:h-16" />
            </a>

            <nav
              className="hidden flex-1 flex-wrap items-center justify-end gap-6 text-sm xl:flex xl:gap-8 xl:text-base"
              aria-label="Hauptnavigation"
            >
              <div className="flex flex-wrap items-center justify-end gap-6 xl:gap-10">
                {t.landing.navItems.map((item) => (
                  <a key={item.href} className="text-blue no-underline hover:underline text-[22px]" href={item.href}>
                    {t.landing.nav[item.key as keyof typeof t.landing.nav]}
                  </a>
                ))}
              </div>
              <ContactButton />
            </nav>

            <button
              type="button"
              className="inline-flex h-11 w-11 shrink-0 flex-col items-center justify-center gap-1.5 rounded bg-white p-2 text-blue xl:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              aria-expanded={mobileOpen}
              aria-controls="landing-mobile-nav-new"
              aria-label={mobileOpen ? t.header.closeMainNav : t.header.openMainNav}
            >
              <span
                className={`block h-0.5 w-6 bg-blue transition-transform duration-200 ease-out ${mobileOpen ? 'translate-y-[8px] rotate-[45deg]' : ''}`}
              />
              <span className={`block h-0.5 w-6 bg-blue transition-opacity duration-200 ${mobileOpen ? 'opacity-0' : ''}`} />
              <span
                className={`block h-0.5 w-6 bg-blue transition-transform duration-200 ease-out ${
                  mobileOpen ? '-translate-y-[8px] -rotate-[45deg]' : ''
                }`}
              />
            </button>
          </div>
        </LargeContainer>
      </div>

      <div
        id="landing-mobile-nav-new"
        style={{ top: headerOffset }}
        className={`fixed inset-x-0 bottom-0 z-40 overflow-y-auto border-t border-blue bg-white xl:hidden ${mobileOpen ? 'flex flex-col' : 'hidden'}`}
      >
        <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col px-3 py-6 sm:px-6">
          <nav className="flex min-h-0 flex-1 flex-col justify-between gap-8" aria-label="Hauptnavigation">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 border-b border-lightGray pb-4 text-sm text-blue">
                <a href={f.phoneHref} className="text-blue no-underline hover:underline" onClick={closeMobile}>
                  {f.phone}
                </a>
                <a href={f.emailHref} className="text-blue no-underline hover:underline" onClick={closeMobile}>
                  {f.email}
                </a>
              </div>
              {t.landing.navItems.map((item) => (
                <a key={item.href} className="text-lg font-[200] text-blue no-underline hover:underline" href={item.href} onClick={closeMobile}>
                  {t.landing.nav[item.key as keyof typeof t.landing.nav]}
                </a>
              ))}
              <ContactButton />
            </div>
          </nav>
        </div>
      </div>
    </header>
  )
}

export default LandingHeaderNew
